import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useTranslation } from 'react-i18next'
import {
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  type MetaFunction,
  data,
  useFetcher,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import BackToSignIn from '#app/components/back-to-sign-in.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import OnboardingShapes from '#app/components/onboarding-shapes.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { getInstance, getLocale } from '#app/middleware/i18next.ts'
import { prisma } from '#app/utils/db.server.ts'
import { siteTitle } from '#app/utils/global.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { prepareVerification } from '#app/utils/otp.server.ts'
import { sendResetPasswordEmail } from '#app/utils/reset-password.tsx'
import { Status } from '#app/utils/status.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'

const ForgotPasswordSchema = z.object({
  email: EmailSchema,
})

export async function loader({ context }: LoaderFunctionArgs) {
  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = await i18next.getFixedT(locale)

  const pageTitle = t('forgotPassword.title')

  return { pageTitle }
}

export async function action({ context, request }: ActionFunctionArgs) {
  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = await i18next.getFixedT(locale)

  const formData = await request.formData()
  await checkHoneypot(formData)

  const submission = await parseWithZod(formData, {
    schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
      const user = await prisma.user.findFirst({
        where: { email: data.email },
        select: { id: true },
      })

      if (!user) {
        ctx.addIssue({
          path: ['email'],
          code: z.ZodIssueCode.custom,
          message: 'errors.user.email.notFound',
        })
        return
      }
    }),
    async: true,
  })

  if (submission.status !== 'success') {
    return data(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const { email } = submission.value

  const user = await prisma.user.findFirstOrThrow({
    where: { email },
    select: { email: true, firstName: true },
  })

  const verifyUrl = await prepareVerification({
    period: 10 * 60, // 10 minutes
    request,
    type: 'reset-password',
    email,
  })

  const response = await sendResetPasswordEmail({
    email: user.email,
    firstName: user.firstName,
    t,
    verifyUrl: verifyUrl.toString(),
    baseUrl: getDomainUrl(request),
  })

  if (response.status === Status.Success) {
    return { status: Status.Success, result: submission }
  } else {
    return data(
      { result: submission.reply({ formErrors: [response.error.message] }) },
      { status: 500 },
    )
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const pageTitle = data?.pageTitle ?? 'Error'
  return [{ title: `${pageTitle} | ${siteTitle}` }]
}

export default function ForgotPasswordRoute() {
  const forgotPassword = useFetcher<typeof action>()
  const { t } = useTranslation()

  const [form, fields] = useForm({
    id: 'forgot-password-form',
    constraint: getZodConstraint(ForgotPasswordSchema),
    lastResult: forgotPassword.data?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ForgotPasswordSchema })
    },
    shouldRevalidate: 'onInput',
  })

  const emailWasSuccessfullySent =
    forgotPassword.data?.result?.status === 'success'

  return (
    <main
      id="main-content"
      className="container flex flex-col items-center justify-center pb-32"
    >
      <div className="relative w-[460px]">
        <OnboardingShapes />
        <div className="relative">
          <div className="mt-44">
            <h1 className="font-poppins text-4 font-medium">
              {t('forgotPassword.header')}
            </h1>
            <p className="text-2 mt-3">{t('forgotPassword.instructions')}</p>
          </div>
        </div>
        <forgotPassword.Form method="POST" {...getFormProps(form)}>
          <HoneypotInputs />
          <div className="mt-6">
            <Field
              labelProps={{
                htmlFor: fields.email.id,
                children: t('forgotPassword.fieldLabel'),
              }}
              inputProps={{
                ...getInputProps(fields.email, { type: 'email' }),
                autoFocus: true,
                autoComplete: 'email',
                placeholder: t('signIn.emailFieldPlaceholder'),
              }}
              errors={fields.email.errors}
            />
          </div>
          <ErrorList errors={form.errors} id={form.errorId} />

          <div>
            {emailWasSuccessfullySent && (
              <p className="text-2 mb-4">
                {t('passwordReset.emailSuccessfullySent')}
              </p>
            )}
            {!emailWasSuccessfullySent && (
              <StatusButton
                status={
                  forgotPassword.state === 'submitting'
                    ? 'pending'
                    : (form.status ?? 'idle')
                }
                type="submit"
                disabled={forgotPassword.state !== 'idle'}
              >
                {t('forgotPassword.buttonText')}
              </StatusButton>
            )}
          </div>
        </forgotPassword.Form>

        <BackToSignIn />
      </div>
    </main>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
